<template>
    <div id="order-search-view"
         class="full-vue-page">
        <div class="container container--top-padding-for-mobile-search">
            <div class="two-col-page">
                <div class="two-col-page__sidebar">
                    <OrderSearchForm ref="search-form"
                                     :has-results="results !== null"
                                     @clear="clearForm"
                                     @submit="onFormSubmit" />
                </div>

                <div class="two-col-page__content">
                    <PageLoadingSpinner v-if="showLoader"
                                        color="light" />
                    <OrderSearchResults v-else-if="results && results.orders.length > 0"
                                        :current-page="currentPage"
                                        :page-size="pageSize"
                                        :results="results"
                                        :search-criteria="searchCriteria" />
                    <div v-else-if="results">
                        <div class="alert alert-warning lead">
                            {{ l('ORDERS.NO_RESULTS') }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="footer-btn-bar"
             :class="{visible: bottomBarVisible}">
            <div class="footer-btn-bar__left"></div>
            <div class="footer-btn-bar__right">
                <Pager v-model="currentPage"
                       :total-items="results ? results.orders.length : 0"
                       :page-size="pageSize" />
            </div>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import EventNames from '@/lib/Events/EventNames';
import OrderSearchForm from './components/OrderSearchForm';
import OrderSearchResults from './components/OrderSearchResults';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import Pager from '@/vue/components/Pager';
import PrivilegeNames from '@/lib/Privileges/PrivilegeNames';
import { container } from '@/container';
import { createOrderSearchCriteria, updateOrderSearchCriteria } from '@/lib/Orders/Search';
import { mapState } from 'vuex';
import { timeoutPromise } from '@/functions/functions';

export default {
    name: 'OrderSearchView',

    components: {
        PageLoadingSpinner,
        Pager,
        OrderSearchResults,
        OrderSearchForm,
    },

    data() {
        return {
            showLoader: false,

            // Replaced with an actual function in 'created'.
            debouncedSearch: null,
        };
    },

    computed: {
        ...mapState('orderSearch', [
            'searchCriteria',
            'results',
            'pageSize',
        ]),

        currentPage: {
            get() {
                return this.$store.state.orderSearch.currentPage;
            },
            set(page) {
                this.$store.commit('orderSearch/setCurrentPage', page);
            },
        },

        bottomBarVisible() {
            return !this.showLoader && !!this.results && (this.results.orders.length > this.pageSize);
        },
    },

    watch: {
        bottomBarVisible: {
            handler(newValue) {
                this.$store.commit('setBottomBarVisible', newValue);
            },
            immediate: true,
        },

        currentPage() {
            this.$nextTick(() => {
                if (document.getElementById('order-search-view')) {
                    document.getElementById('order-search-view').scrollTop = 0;
                }
            });
        },
    },

    async created() {
        this.debouncedSearch = _.debounce(
            (searchCriteria, wasBarcodeScan) => this.doSearch(searchCriteria, wasBarcodeScan),
            500,
            {
                leading: true,
                trailing: false,
            },
        );

        container.navigationService.showNav();

        // Check user has permission to view this page.
        await container.privilegesManager.requirePrivilegeOr403(PrivilegeNames.ACCESS_REDEEM);

        // On a mobile device with built-in scanner e.g. CT-50 we can only capture the scan into an element with focus.
        // Unfortunately any element that has focus (except as a result of the initial onload) triggers the
        // virtual keyboard to appear, therefore the search field cannot permanently have focus.
        //
        // This method listens for the start of a scan and when it detects it set the focus to the search field
        // to capture the scan.
        //
        // On a CT-50 F11 is the code for the right scan button and F12 is the code for the left scan button.
        this.deregisterKeyDownListenerFunc = container.$rootScope.$on(EventNames.KEY_DOWN, (event, originalEvent) => {
            const scanStartKeys = ['F11', 'F12'];
            if (scanStartKeys.includes(originalEvent.code)) {
                console.log('F11 or F12 scanner key detected');
                const searchInput = document.getElementById('order-search-input');
                searchInput.focus();
                searchInput.value = '';
            }
        });

        this.deregisterBarCodeScannedListenerFunc = container.$rootScope.$on(
            EventNames.BARCODE_SCANNED,
            (event, response) => {
                console.log('Barcode Scanned (Order Search)', response.code);
                this.onBarcodeScan(response.code);
            }
        );
    },

    mounted() {
        // If there is a "scannedBarcode" query in the URL clear any existing results and search
        // for that query.
        // For this we need to use Angular's $location.
        const locationSearch = container.$location.search();
        if (locationSearch.scannedBarcode) {
            const scannedBarcode = locationSearch.scannedBarcode;
            // Remove scannedBarcode from the URL so it does not happen again if the back button is used.
            container.$location.replace('/redeem').search({});

            const searchCriteria = createOrderSearchCriteria();
            searchCriteria.search = scannedBarcode;
            updateOrderSearchCriteria(searchCriteria);

            this.doSearch(searchCriteria, false);
        } else {
            if (this.searchCriteria) {
                // If there was a previous search fill the form with the cached search criteria.
                this.$refs['search-form'].setFormData(this.searchCriteria);

                // If a session was set in the search criteria this means we must have come from
                // the TodayView. Automatically do the search.
                if (this.searchCriteria.sessionWithEvent && this.results === null) {
                    this.$refs['search-form'].submit();
                }
            }

            if (this.results !== null && this.$store.state.orderSearch.resultsValid === false) {
                // Redo the search.
                this.$refs['search-form'].submit();
            }
        }
    },

    beforeUnmount() {
        this.$store.commit('setBottomBarVisible', false);

        this.debouncedSearch = null;
        if (this.deregisterKeyDownListenerFunc) {
            this.deregisterKeyDownListenerFunc();
        }
        if (this.deregisterBarCodeScannedListenerFunc) {
            this.deregisterBarCodeScannedListenerFunc();
        }
    },

    methods: {
        /**
         * This handles a scan when the window has focus rather than the input field.
         *
         * @param {string} search
         */
        onBarcodeScan(search) {
            this.clearForm();

            const searchCriteria = createOrderSearchCriteria();
            searchCriteria.search = search;
            updateOrderSearchCriteria(searchCriteria);

            if (this.$refs['search-form']) {
                this.$refs['search-form'].setFormData(searchCriteria);
            }

            this.debouncedSearch(searchCriteria, true);
        },

        clearForm() {
            this.$store.commit('orderSearch/clear');
            this.error = null;
            this.showLoader = false;
        },

        /**
         * Called when the search form is directly rather than by a scan when the window has focus.
         *
         * @param {OrderSearchCriteria} searchCriteria
         */
        async onFormSubmit(searchCriteria) {
            // A scan when the input has focus will trigger both a BACODE_SCANNED event and submit due to the carriage
            // return added by the scanner. The order of scan event and form submit is not consistent across devices
            // and scanners therefore wait a fraction of a second to ensure that the BARCODE_SCANNED event has had time
            // to propagate first. Note: the debounce period on debouncedSearch is 500ms.
            await timeoutPromise(300);
            // There is a chance that a barcode scan was picked up and we have navigated away from the page
            // while waiting for the timeout. this.debouncedSearch gets cleared in the beforeUnmount method,
            // so check to see if that is still to know if this component is still mounted.
            if (typeof this.debouncedSearch === 'function') {
                this.debouncedSearch(searchCriteria);
            }
        },

        /**
         * @param {OrderSearchCriteria} searchCriteria
         * @param {boolean=false} [wasBarcodeScan] FIXME: This is also in the OrderSearchCriteria.
         */
        doSearch(searchCriteria, wasBarcodeScan = false) {
            this.showLoader = true;
            this.error = null;
            searchCriteria.search = searchCriteria.search.trim();

            // Only redirect if a search query was used. This is probably a search for a booking ref.
            // If another way was used to find the order, don't redirect so the user can clearly see there was
            // just one result.
            const redirectIfOneOrder = !!searchCriteria.search;

            container.orderSearcher.search(searchCriteria)
                .then((results) => {
                    container.redemptionScanHandler.redirectFromSearchResults(
                        searchCriteria.search,
                        results.orders,
                        wasBarcodeScan,
                        redirectIfOneOrder
                    );

                    this.searchResultsLoaded(searchCriteria, results);
                });
        },

        /**
         * @param {OrderSearchCriteria} searchCriteria
         * @param {OrderSearchResults} results
         */
        searchResultsLoaded(searchCriteria, results) {
            // Store the results in Vuex.
            // The results in the view come from Vuex, so setting this causes them to be displayed.
            this.$store.commit('orderSearch/setResults', { searchCriteria, results });

            // Reset the current page when we search to prevent errors like page 7/5 after multiple searches.
            this.$store.commit('orderSearch/setCurrentPage', 1);

            if (this.$refs['search-form']) {
                // Update the search query displayed in the form in case of a race condition,
                // so the user can see what the results are for.
                // The if is in case the results are loaded before the form is mounted on screen.
                this.$refs['search-form'].setFormData(searchCriteria);
            }

            this.showLoader = false;

            if (this.$refs['search-form']) {
                // If screen is small enough to be a mobile device we don't want to leave the focus on the input
                // as the keyboard takes up half the screen.
                // However, on desktop we want to focus on the search so you can quickly type/scan another barcode.
                // Focus search on desktop only.
                this.$refs['search-form'].focusSearchInput(false, true);
                // Blur search on mobile only.
                this.$refs['search-form'].blurSearchInput(true, false);
            }
        },
    },
};
</script>

<style lang="less">
@import (reference) "../../../../less/variables";

@media (max-width: @mobile-max) {
    #order-search-view {
        .page-loading-spinner {
            color: @dark-blue;
        }

        .two-col-page .two-col-page__sidebar {
            margin-bottom: 0;
        }

        .search-page-form__clear-btn i {
            top: 18px;
        }
    }
}
</style>
